import { FC } from "react";
import {
  AccordionHeader,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiTypography,
} from "../../../../../../components";
import { isEmpty } from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { CompleteBulkTransferResult } from "../../../../../createBulkTransfer/components/steps/bulkTransferSummaryResult/BulkTransferSummaryResult";
import {
  TransferParticipantResult,
  transferTypeKey,
} from "../../../../../createBulkTransfer/modelsAndSchemas/Models";
import { bulkTransferSiteCoreKeys } from "../../../../../createBulkTransfer/siteCore/SiteCoreKeys";
import {
  ParticipantData,
  SelectedParticipantResultType,
} from "../../../../modelsAndSchemas/Models";
import { bulkActivitySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { SelectedParticipantResult } from "../../bulkActivityDetails/selectedParticipantResult/SelectedParticipantResult";
import { BulkSummaryResultWrapper } from "./BulkSummaryResult.style";

interface BulkSummaryResultProps {
  open: boolean;
  setShowViewSummaryModal: (value: boolean) => void;
  summaryData: ParticipantData | TransferParticipantResult;
  currentStep: number;
  pageId: string;
  transferType?: string;
}

const BulkSummaryResult: FC<BulkSummaryResultProps> = ({
  open,
  setShowViewSummaryModal,
  summaryData,
  currentStep,
  pageId,
  transferType = "",
}) => {
  const { fetchLabel, fetchComponentTitle, commonLabels } = useSiteCore();
  const getLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.summaryTable, key);
  };
  const getTransferLabel = (key: string) => {
    return fetchLabel(bulkTransferSiteCoreKeys.bulkTransferSummary, key);
  };
  const noUnProcessedParticipants = summaryData?.unProcessedParticipants !== 0;
  const noProcessedParticipants = summaryData?.processedParticipants !== 0;

  const renderHeader = (
    title: string,
    selectedCount: number,
    isUnprocessed?: boolean,
  ) => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid size={{ xs: 6 }} className="form-header-left">
          <MuiBox className="title-container">
            <MuiTypography
              variant="h2SemiBold"
              className="form-header-left"
              component="h2"
            >
              {title}
            </MuiTypography>
            <MuiTypography
              variant="h3Bold"
              className={`selected-count ${
                isUnprocessed ? "unprocessed" : "processed"
              }`}
            >
              {selectedCount}
            </MuiTypography>
          </MuiBox>
        </MuiGrid>
      </MuiGrid>
    );
  };
  const isFromTransferFlow = !isEmpty(transferType);

  const bulkTransferTitleFinal =
    transferType === transferTypeKey.changeCountry
      ? getTransferLabel(bulkTransferSiteCoreKeys.bulkCountryTransfer)
      : getTransferLabel(bulkTransferSiteCoreKeys.bulkProgramTransfer);
  return (
    <MuiModal
      onClose={() => setShowViewSummaryModal(false)}
      title={
        isFromTransferFlow
          ? bulkTransferTitleFinal
          : fetchComponentTitle(bulkActivitySiteCoreKeys.summaryTable)
      }
      open={open}
      secondaryButtonTitle={commonLabels.close}
      hasLargeContent
      onSecondaryButtonClick={() => setShowViewSummaryModal(false)}
    >
      <BulkSummaryResultWrapper>
        {noProcessedParticipants && (
          <AccordionHeader
            wrapperClassName="activity-details-accordion"
            title={renderHeader(
              isFromTransferFlow
                ? getTransferLabel(bulkTransferSiteCoreKeys.processed)
                : getLabel(bulkActivitySiteCoreKeys.processed),
              summaryData?.processedParticipants ?? 0,
            )}
            isExpanded={true}
          >
            {isFromTransferFlow ? (
              <CompleteBulkTransferResult
                rows={
                  (summaryData as TransferParticipantResult)?.processed ?? []
                }
                pageId={pageId}
                transferType={transferType}
              />
            ) : (
              <SelectedParticipantResult
                rows={
                  ((summaryData as ParticipantData)
                    ?.processedResponse as SelectedParticipantResultType[]) ??
                  []
                }
                pageId={pageId}
                currentStep={currentStep}
                isSummaryPage
              />
            )}
          </AccordionHeader>
        )}
        {noUnProcessedParticipants && (
          <AccordionHeader
            wrapperClassName="activity-details-accordion"
            title={renderHeader(
              isFromTransferFlow
                ? getTransferLabel(bulkTransferSiteCoreKeys.unProcessed)
                : getLabel(bulkActivitySiteCoreKeys.unProceseed),
              summaryData?.unProcessedParticipants ?? 0,
              true,
            )}
            isExpanded={true}
          >
            {isFromTransferFlow ? (
              <CompleteBulkTransferResult
                rows={
                  (summaryData as TransferParticipantResult)?.unProcessed ?? []
                }
                pageId={pageId}
                transferType={transferType}
                isUnprocessed
              />
            ) : (
              <SelectedParticipantResult
                rows={
                  ((summaryData as ParticipantData)
                    ?.unProcessedResponse as SelectedParticipantResultType[]) ??
                  []
                }
                pageId={pageId}
                currentStep={currentStep}
                isSummaryPage
                isUnprocessed
              />
            )}
          </AccordionHeader>
        )}
      </BulkSummaryResultWrapper>
    </MuiModal>
  );
};

export default BulkSummaryResult;
